import Navigation from "../components/Navigation";
import Seo from "../components/Seo";
import LogoStrip from "../components/LogoStrip";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import {
  Bot,
  GraduationCap,
  Headset,
  Presentation,
  Wrench,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";

// Cornerstone-/Leistungsseite fuer "KI-Berater Versicherung" / "KI im Versicherungsvertrieb".
// Deutsch hardcoded (prerendert = indexiert). Echte E-E-A-T inkl. Buch zum Thema.

const TITLE =
  "KI in der Versicherung: Beratung für Versicherer, Makler & Vertrieb – Daaniyal Khan";
const DESCRIPTION =
  "KI- und Digitalisierungsberatung für die Versicherungsbranche: GenAI und ChatGPT im Versicherungsvertrieb, Produkt- und Tarif-Assistenten, Onboarding und Automatisierung — für Versicherer, Maklerpools und Vertriebsorganisationen im DACH-Raum.";

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "KI-Beratung für die Versicherungsbranche",
  name: "KI in der Versicherung – Beratung, Workshops und Umsetzung",
  description: DESCRIPTION,
  provider: {
    "@type": "Person",
    name: "Daaniyal Khan",
    url: "https://www.daaniyalkhan.com",
    jobTitle: "KI-, Vertriebs- und Digitalisierungsberater",
  },
  areaServed: ["DE", "AT", "CH"],
  audience: {
    "@type": "Audience",
    audienceType:
      "Versicherer, Maklerpools, Ausschließlichkeitsorganisationen, Vertriebsgesellschaften",
  },
  url: "https://www.daaniyalkhan.com/ki-versicherung",
};

const FIELDS = [
  {
    icon: Bot,
    title: "Produkt- & Tarif-Assistent",
    body: "Ein KI-Assistent, der alle Produkte und Tarife kennt — Vermittler bekommen verlässliche Auskunft in Sekunden, statt auf Rückruf zu warten. Auch direkt im Kundentermin.",
  },
  {
    icon: GraduationCap,
    title: "Onboarding & Wissenssicherung",
    body: "Neue Vermittler werden schneller produktiv, Spezialwissen bleibt verfügbar — unabhängig davon, wer gerade im Innendienst erreichbar ist.",
  },
  {
    icon: Headset,
    title: "Service- & Support-Entlastung",
    body: "Wiederkehrende Produktfragen fängt die KI ab. Innendienst und Support gewinnen Kapazität für die Fälle, die wirklich Menschen brauchen.",
  },
];

const FORMATS = [
  {
    icon: Presentation,
    title: "Keynote & Impuls",
    body: "Realitäts-Check statt Hype: wo GenAI in Versicherung und Vertrieb wirkt — für Vorstand, Führungskreis oder Maklerveranstaltung.",
  },
  {
    icon: GraduationCap,
    title: "Workshop",
    body: "Praxis-Workshop für Vertriebs- und Fachbereiche: konkrete Use-Cases, Prompts und Abläufe für Ihre Produktwelt.",
  },
  {
    icon: Wrench,
    title: "Umsetzungsbegleitung",
    body: "Vom ersten Use-Case bis zum laufenden Betrieb — inklusive Datenbasis, Akzeptanz im Vertrieb und regulatorischem Rahmen.",
  },
  {
    icon: ShieldCheck,
    title: "Governance & Rahmen",
    body: "Sicherer KI-Einsatz: was im Versicherungskontext erlaubt ist, was nicht — und wie man Falschaussagen und Risiken vermeidet.",
  },
];

const FAQ = [
  {
    q: "Ersetzt KI die Vermittler?",
    a: "Nein. KI nimmt Produkt- und Wissensarbeit ab und macht Vermittler schneller und sicherer — die Beratung und der Abschluss bleiben beim Menschen.",
  },
  {
    q: "Für wen ist das relevant?",
    a: "Für Versicherer, Maklerpools, Ausschließlichkeitsorganisationen und Vertriebsgesellschaften im DACH-Raum — überall, wo viele Vermittler verlässliche Produkt- und Tarifauskünfte brauchen.",
  },
  {
    q: "Was ist mit Datenschutz und Aufsicht?",
    a: "Der regulatorische Rahmen wird von Anfang an mitgedacht — von der Datenbasis bis zur Frage, welche Auskünfte die KI geben darf und welche nicht.",
  },
];

const KiVersicherung = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Seo title={TITLE} description={DESCRIPTION} />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-[#C5A66A] focus:text-[#0F1B2D] focus:px-4 focus:py-2 focus:rounded focus:font-semibold"
      >
        Zum Hauptinhalt springen
      </a>
      <Navigation />
      <main id="main-content" className="pt-32 pb-20">
        <div className="container mx-auto px-6 max-w-4xl">
          <header className="mb-16 max-w-3xl">
            <div className="text-sm text-[#C5A66A] uppercase tracking-wider font-semibold mb-4">
              KI-Beratung Versicherung · DACH
            </div>
            <h1 className="font-playfair text-4xl lg:text-6xl font-bold text-[#0F1B2D] mb-6 leading-tight">
              KI in der Versicherung — vom Hype zum messbaren Nutzen im Vertrieb
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              Die Versicherungsbranche ist mein Zuhause. Über ein Jahrzehnt
              Vertrieb und Digitalisierung im Finanz- und Versicherungsumfeld,
              ein Buch zum Thema „KI &amp; ChatGPT im Versicherungsvertrieb" — und
              umgesetzte Projekte statt Folien. Hier zeige ich, wo GenAI für
              Versicherer und ihren Vertrieb wirklich etwas bewegt.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/#contact"
                className="inline-flex items-center bg-[#C5A66A] text-[#0F1B2D] px-7 py-3 rounded-lg font-semibold hover:bg-[#b8966b] hover:scale-105 transition-all duration-300"
              >
                Strategie-Gespräch buchen
                <ArrowRight size={18} className="ml-2" />
              </Link>
              <Link
                to="/ki-vertrieb"
                className="inline-flex items-center px-7 py-3 rounded-lg font-semibold text-[#0F1B2D] border border-gray-300 hover:border-[#C5A66A] transition-colors"
              >
                KI im Vertrieb (allgemein)
              </Link>
            </div>
          </header>

          {/* Für wen */}
          <section className="mb-16">
            <h2 className="font-playfair text-3xl font-bold text-[#0F1B2D] mb-4">
              Für wen
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Versicherer, Maklerpools, Ausschließlichkeitsorganisationen und
              Vertriebsgesellschaften im DACH-Raum — überall dort, wo viele
              Vermittler schnelle, verlässliche Produkt- und Tarifauskünfte
              brauchen und der Innendienst entlastet werden soll.
            </p>
          </section>

          {/* Wo KI wirkt */}
          <section className="mb-16">
            <h2 className="font-playfair text-3xl font-bold text-[#0F1B2D] mb-8">
              Wo KI in der Versicherung wirkt
            </h2>
            <div className="grid gap-6 md:grid-cols-3">
              {FIELDS.map(({ icon: Icon, title, body }) => (
                <div
                  key={title}
                  className="bg-[#FAFAF8] rounded-2xl border border-gray-200 p-6"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#C5A66A]/10 flex items-center justify-center mb-4">
                    <Icon size={24} className="text-[#C5A66A]" aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-lg text-[#0F1B2D] mb-2">{title}</h3>
                  <p className="text-gray-600 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-gray-600 leading-relaxed">
              Zunehmend übernimmt ein KI-Agent den Vergleich für den Kunden. Was
              Accentures Befragung von 25.590 Menschen dazu zeigt und worauf sich
              Versicherer und Vermittler einstellen sollten, steht im Beitrag{" "}
              <a
                href="/insights/ki-agenten-versicherungsvertrieb"
                className="text-[#C5A66A] underline"
              >
                Wenn die KI den Versicherer auswählt
              </a>
              .
            </p>
          </section>

          {/* Case */}
          <section className="mb-16">
            <h2 className="font-playfair text-3xl font-bold text-[#0F1B2D] mb-6">
              Aus der Praxis: Produkt- & Tarif-Assistent bei einem Versicherer
            </h2>
            <div className="bg-[#0F1B2D] rounded-2xl p-8 text-white">
              <div className="grid gap-8 md:grid-cols-3 mb-8">
                <div>
                  <div className="text-4xl font-bold text-[#C5A66A] mb-1">
                    ~4.000
                  </div>
                  <div className="text-sm text-gray-300">
                    Vermittler mit Zugriff (plus weitere Nutzer)
                  </div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-[#C5A66A] mb-1">
                    2–8 Std → Sek.
                  </div>
                  <div className="text-sm text-gray-300">
                    Tarifauskunft: vorher Rückruf, jetzt sofort
                  </div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-[#C5A66A] mb-1">
                    alle Tarife
                  </div>
                  <div className="text-sm text-gray-300">
                    ein Assistent für die gesamte Produktwelt
                  </div>
                </div>
              </div>
              <p className="text-gray-200 leading-relaxed mb-4">
                Bei einem Versicherungsunternehmen liefen Produkt- und
                Tariffragen über eine Support-Hotline mit reiner Rückruffunktion —
                Vermittler warteten zwei bis acht Stunden auf eine Antwort. Wir
                haben einen KI-Assistenten bereitgestellt, der die gesamte
                Produkt- und Tarifwelt kennt und jederzeit per Sprache oder Text
                Auskunft gibt — auch direkt beim Kunden. Derselbe Assistent
                unterstützt zusätzlich den internen Support.
              </p>
              <p className="text-gray-200 leading-relaxed">
                Wirkung: schnellere und sichere Auskünfte, ein spürbar
                entlasteter Innendienst, schnelleres Onboarding neuer Vermittler
                und weniger Falschaussagen im Kundentermin — Führungskräfte können
                neuen Mitarbeitern früher vertrauen und sich auf den Vertrieb
                konzentrieren.
              </p>
              <p className="mt-6 text-xs text-gray-400">
                Anonymisiert. Details auf Anfrage.
              </p>
            </div>
          </section>

          {/* Formate */}
          <section className="mb-16">
            <h2 className="font-playfair text-3xl font-bold text-[#0F1B2D] mb-8">
              Formate der Zusammenarbeit
            </h2>
            <div className="grid gap-6 md:grid-cols-2">
              {FORMATS.map(({ icon: Icon, title, body }) => (
                <div
                  key={title}
                  className="flex gap-4 bg-white rounded-2xl border border-gray-200 p-6"
                >
                  <div className="w-12 h-12 shrink-0 rounded-xl bg-[#C5A66A]/10 flex items-center justify-center">
                    <Icon size={24} className="text-[#C5A66A]" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-[#0F1B2D] mb-1">
                      {title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">{body}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Warum DK */}
          <section className="mb-16">
            <h2 className="font-playfair text-3xl font-bold text-[#0F1B2D] mb-4">
              Warum Daaniyal Khan
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Ich kenne die Versicherungsbranche von innen — über ein Jahrzehnt
              Vertrieb und Digitalisierung im Finanz- und Versicherungsumfeld im
              DACH-Raum. Zum Einsatz von KI im Versicherungsvertrieb habe ich auch
              ein Buch geschrieben:{" "}
              <a
                href="https://www.amazon.de/dp/B0BTGKRW19"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#C5A66A] underline"
              >
                „Künstliche Intelligenz &amp; ChatGPT – der Einsatz im
                Versicherungsvertrieb"
              </a>
              . Einordnung im Interview bei{" "}
              <a
                href="https://www.businesstalk-kudamm.com/videos/digitalisierung-von-finanz-und-versicherungsvertrieben-daaniyal-khan-barmenia-gruppe/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#C5A66A] underline"
              >
                Businesstalk am Kudamm
              </a>
              . Alle Titel auf der{" "}
              <Link to="/buecher" className="text-[#C5A66A] underline">
                Bücher-Seite
              </Link>
              .
            </p>
          </section>

          {/* Partner & Referenzen */}
          <section className="mb-16">
            <h2 className="font-playfair text-3xl font-bold text-[#0F1B2D] mb-6">
              Partner &amp; Referenzen
            </h2>
            <LogoStrip />
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="font-playfair text-3xl font-bold text-[#0F1B2D] mb-8">
              Häufige Fragen
            </h2>
            <div className="space-y-6">
              {FAQ.map(({ q, a }) => (
                <div key={q}>
                  <h3 className="font-bold text-lg text-[#0F1B2D] mb-2">{q}</h3>
                  <p className="text-gray-600 leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-r from-[#0F1B2D] to-[#1a2d4a] rounded-2xl p-10 text-white text-center">
            <h2 className="text-3xl font-bold mb-4 text-[#C5A66A]">
              KI in Ihrer Versicherung?
            </h2>
            <p className="text-lg mb-8 max-w-2xl mx-auto">
              Ein 90-Minuten-Gespräch klärt, wo GenAI in Ihrem Haus am schnellsten
              Wirkung zeigt — und wo Vorsicht angebracht ist.
            </p>
            <Link
              to="/#contact"
              className="inline-block bg-[#C5A66A] text-[#0F1B2D] px-8 py-4 rounded-lg font-semibold hover:bg-[#b8966b] hover:scale-105 transition-all duration-300"
            >
              Kostenloses Erstgespräch
            </Link>
          </section>
        </div>
      </main>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
    </div>
  );
};

export default KiVersicherung;
